const TARGET_SAMPLE_RATE = 16000;
const DEFAULT_MOTION_FPS = 30;
const RING_SECONDS = 12;

type AudioMode = "idle" | "mic" | "element";

export class MicFeatureStream {
  private context: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private stream: MediaStream | null = null;
  private element: HTMLAudioElement | null = null;
  private processor: ScriptProcessorNode | null = null;
  private silenceGain: GainNode | null = null;
  private frequencyBins: Float32Array<ArrayBuffer> | null = null;
  private waveformBins: Float32Array<ArrayBuffer> | null = null;
  private mode: AudioMode = "idle";
  private ring = new Float32Array(TARGET_SAMPLE_RATE * RING_SECONDS);
  private ringWrite = 0;
  private writtenSamples = 0;
  private decodedElementAudio: Float32Array<ArrayBuffer> | null = null;

  async start(): Promise<void> {
    await this.stop();
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    });
    this.context = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
    const source = this.context.createMediaStreamSource(this.stream);
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0.65;
    this.processor = this.context.createScriptProcessor(2048, 1, 1);
    this.processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      const resampled = resampleLinear(input, event.inputBuffer.sampleRate, TARGET_SAMPLE_RATE);
      this.appendRing(resampled);
    };
    this.silenceGain = this.context.createGain();
    this.silenceGain.gain.value = 0;
    source.connect(this.analyser);
    source.connect(this.processor);
    this.processor.connect(this.silenceGain);
    this.silenceGain.connect(this.context.destination);
    this.frequencyBins = new Float32Array(this.analyser.frequencyBinCount);
    this.waveformBins = new Float32Array(this.analyser.fftSize);
    this.mode = "mic";
  }

  async startElement(url: string): Promise<void> {
    await this.stop();
    this.context = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
    this.decodedElementAudio = await decodeAudioUrl(url, this.context);
    this.element = new Audio(url);
    this.element.crossOrigin = "anonymous";
    this.element.loop = false;
    this.element.preload = "auto";
    const source = this.context.createMediaElementSource(this.element);
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0.65;
    source.connect(this.analyser);
    this.analyser.connect(this.context.destination);
    this.frequencyBins = new Float32Array(this.analyser.frequencyBinCount);
    this.waveformBins = new Float32Array(this.analyser.fftSize);
    this.mode = "element";
    await this.element.play();
  }

  async stop(): Promise<void> {
    if (this.element) {
      this.element.pause();
      this.element.src = "";
      this.element.load();
    }
    this.processor?.disconnect();
    this.silenceGain?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    await this.context?.close();
    this.context = null;
    this.analyser = null;
    this.stream = null;
    this.element = null;
    this.processor = null;
    this.silenceGain = null;
    this.frequencyBins = null;
    this.waveformBins = null;
    this.mode = "idle";
    this.ring.fill(0);
    this.ringWrite = 0;
    this.writtenSamples = 0;
    this.decodedElementAudio = null;
  }

  nextWaveformForFrame(
    frameIndex: number,
    samples = 2048,
    fps = DEFAULT_MOTION_FPS
  ): Float32Array<ArrayBuffer> | null {
    const endSample = Math.max(0, Math.round(((frameIndex + 1) / fps) * TARGET_SAMPLE_RATE));
    const startSample = endSample - samples;
    if (this.mode === "element") {
      return windowFromDecodedAudio(this.decodedElementAudio, startSample, samples);
    }
    if (this.mode === "mic") {
      if (this.writtenSamples < endSample) {
        return null;
      }
      return windowFromRing(this.ring, this.ringWrite, this.writtenSamples, startSample, samples);
    }
    return new Float32Array(samples);
  }

  nextWaveform(samples = 2048): Float32Array<ArrayBuffer> {
    const out = new Float32Array(samples);
    if (!this.analyser || !this.waveformBins) {
      return out;
    }
    this.analyser.getFloatTimeDomainData(this.waveformBins);
    if (this.waveformBins.length >= samples) {
      out.set(this.waveformBins.subarray(this.waveformBins.length - samples));
      return out;
    }
    out.set(this.waveformBins, samples - this.waveformBins.length);
    return out;
  }

  nextFeature(audioDim = 80): Float32Array<ArrayBuffer> {
    if (!this.analyser || !this.frequencyBins) {
      return new Float32Array(audioDim);
    }
    this.analyser.getFloatFrequencyData(this.frequencyBins);
    const feature = new Float32Array(audioDim);
    const binsPerFeature = Math.max(1, Math.floor(this.frequencyBins.length / audioDim));
    for (let i = 0; i < audioDim; i += 1) {
      let sum = 0;
      let count = 0;
      const start = i * binsPerFeature;
      const end = Math.min(this.frequencyBins.length, start + binsPerFeature);
      for (let j = start; j < end; j += 1) {
        const db = Number.isFinite(this.frequencyBins[j]) ? this.frequencyBins[j] : -120;
        sum += Math.max(-120, Math.min(0, db));
        count += 1;
      }
      const averageDb = count > 0 ? sum / count : -120;
      feature[i] = (averageDb + 120) / 120;
    }
    return feature;
  }

  private appendRing(samples: Float32Array<ArrayBufferLike>): void {
    for (const sample of samples) {
      this.ring[this.ringWrite] = Number.isFinite(sample) ? sample : 0;
      this.ringWrite = (this.ringWrite + 1) % this.ring.length;
      this.writtenSamples += 1;
    }
  }
}

async function decodeAudioUrl(url: string, context: AudioContext): Promise<Float32Array<ArrayBuffer>> {
  const response = await fetch(url, { cache: "force-cache" });
  if (!response.ok) {
    throw new Error(`Failed to load audio: ${response.status}`);
  }
  const decoded = await context.decodeAudioData(await response.arrayBuffer());
  const mono = mixToMono(decoded);
  return resampleLinear(mono, decoded.sampleRate, TARGET_SAMPLE_RATE);
}

function mixToMono(buffer: AudioBuffer): Float32Array<ArrayBuffer> {
  const out = new Float32Array(buffer.length);
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let index = 0; index < data.length; index += 1) {
      out[index] += data[index] / buffer.numberOfChannels;
    }
  }
  return out;
}

function windowFromDecodedAudio(
  audio: Float32Array<ArrayBuffer> | null,
  startSample: number,
  samples: number
): Float32Array<ArrayBuffer> {
  const out = new Float32Array(samples);
  if (!audio) {
    return out;
  }
  for (let dst = 0; dst < samples; dst += 1) {
    const source = startSample + dst;
    if (source >= 0 && source < audio.length) {
      out[dst] = audio[source];
    }
  }
  return out;
}

function windowFromRing(
  ring: Float32Array<ArrayBuffer>,
  ringWrite: number,
  writtenSamples: number,
  startSample: number,
  samples: number
): Float32Array<ArrayBuffer> {
  const out = new Float32Array(samples);
  const oldest = Math.max(0, writtenSamples - ring.length);
  for (let dst = 0; dst < samples; dst += 1) {
    const absolute = startSample + dst;
    if (absolute < 0 || absolute < oldest || absolute >= writtenSamples) {
      continue;
    }
    const ringIndex = (ringWrite - (writtenSamples - absolute) + ring.length) % ring.length;
    out[dst] = ring[ringIndex];
  }
  return out;
}

function resampleLinear(
  input: Float32Array<ArrayBufferLike>,
  sourceRate: number,
  targetRate: number
): Float32Array<ArrayBuffer> {
  if (Math.abs(sourceRate - targetRate) < 1) {
    return new Float32Array(input);
  }
  const outputLength = Math.max(1, Math.round((input.length * targetRate) / sourceRate));
  const output = new Float32Array(outputLength);
  const scale = sourceRate / targetRate;
  for (let index = 0; index < output.length; index += 1) {
    const source = index * scale;
    const left = Math.floor(source);
    const right = Math.min(input.length - 1, left + 1);
    const amount = source - left;
    output[index] = input[left] * (1 - amount) + input[right] * amount;
  }
  return output;
}
